/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';

import {IndexedObject, ModelResponse, PDFElementPersist, PDFElementType} from '@dvrd/dvr-controls';

import PDFTemplate from './pdfTemplateModel';

interface Props extends BaseProps {
    element_type?: PDFElementType;
    width?: number;
    height?: number;
    left?: number;
    top?: number;
    options?: IndexedObject<any>;
    template_id?: string;
    template?: PDFTemplate | any;
    persistent?: PDFElementPersist;
    linked_id?: string;
}

interface Fields extends BaseFields {
    element_type: PDFElementType;
    width: number;
    height: number;
    left: number;
    top: number;
    options: IndexedObject<any>;
    template_id: string;
    template: PDFTemplate | null;
    persistent: PDFElementPersist;
    linked_id: string;
}

const fields: Fields = {
    id: '',
    created_at: '',
    updated_at: '',
    element_type: PDFElementType.TEXT,
    width: 0,
    height: 0,
    left: 0,
    top: 0,
    options: {},
    template_id: '',
    template: null,
    persistent: PDFElementPersist.NOT_PERSISTENT,
    linked_id: ''
};

const fieldsDefinition: {[key in keyof Omit<Fields, 'template'>]: FieldDefinition} = {
    id: createFieldDef(ModelFieldType.STRING),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, undefined, true),
    width: createFieldDef(ModelFieldType.NUMBER),
    height: createFieldDef(ModelFieldType.NUMBER),
    left: createFieldDef(ModelFieldType.NUMBER),
    top: createFieldDef(ModelFieldType.NUMBER),
    template_id: createFieldDef(ModelFieldType.STRING),
    options: createFieldDef(ModelFieldType.OBJECT),
    element_type: createFieldDef(ModelFieldType.STRING),
    persistent: createFieldDef(ModelFieldType.STRING, PDFElementPersist.NOT_PERSISTENT),
    linked_id: createFieldDef(ModelFieldType.STRING)
};

const ignoreProps: Array<keyof Props> = [];

export default class PDFElement extends BaseModel<Props, Fields> {
    static get = (_id: string, _callback: ModelResponse<PDFElement>) => {};

    static getAll = (_callback: ModelResponse<PDFElement[]>) => {};

    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
    }

    get id(): string {
        return this.fields.id as string;
    }

    get elementType(): PDFElementType {
        return this.fields.element_type;
    }

    set elementType(element_type: PDFElementType) {
        this.fields.element_type = element_type;
    }

    get width(): number {
        return this.fields.width;
    }

    set width(width: number) {
        this.fields.width = width;
    }

    get height(): number {
        return this.fields.height;
    }

    set height(height: number) {
        this.fields.height = height;
    }

    get left(): number {
        return this.fields.left;
    }

    set left(left: number) {
        this.fields.left = left;
    }

    get top(): number {
        return this.fields.top;
    }

    set top(top: number) {
        this.fields.top = top;
    }

    get options(): IndexedObject<any> {
        return this.fields.options;
    }

    set options(options: IndexedObject<any>) {
        this.fields.options = options;
    }

    get templateId(): string {
        return this.fields.template_id;
    }

    set templateId(template_id: string) {
        this.fields.template_id = template_id;
    }

    get template(): PDFTemplate | null {
        return this.fields.template;
    }

    set template(template: PDFTemplate | null) {
        this.fields.template = template;
        if (template) this.templateId = template.id;
    }

    get persistent(): PDFElementPersist {
        return this.fields.persistent;
    }

    set persistent(persist: PDFElementPersist) {
        this.fields.persistent = persist;
    }

    get linkedId() {
        return this.fields.linked_id;
    }

    set linkedId(id: string) {
        this.fields.linked_id = id;
    }

    save = (_callback?: ModelResponse<PDFElement>) => {};

    update = (_callback?: ModelResponse<PDFElement>) => {};

    delete = (_callback?: ModelResponse<PDFElement>) => {};

    protected build = (props?: Props) => {
        this.constructFields(props);
        if (props) {
            if (props.template) {
                this.template = (
                    props.template instanceof PDFTemplate ? props.template : new PDFTemplate(props.template)
                ).addElement(this);
            }
        }
    };
}
